/**
 * Key Sharing Module
 *
 * This module handles the secure sharing of encryption keys between users.
 * When a user invites another user to access a baby profile, the baby's
 * AES key must be shared securely using asymmetric encryption.
 */

import {
  importPublicKey,
  encryptKeyWithPublicKey,
  decryptKeyWithPrivateKey,
  generateAESKey,
  exportAESKey,
  importAESKey,
  arrayBufferToBase64,
  base64ToArrayBuffer,
} from './keys';

/**
 * Share a baby's encryption key with another user
 *
 * Process:
 * 1. Get the baby's AES key (decrypted with current user's private key)
 * 2. Get the target user's public key
 * 3. Encrypt the baby's AES key with target user's public key
 * 4. Send encrypted key to server for storage
 */
export async function shareKeyWithUser(
  babyAESKey: CryptoKey,
  targetUserPublicKey: string
): Promise<string> {
  const publicKey = await importPublicKey(targetUserPublicKey);
  return encryptKeyWithPublicKey(babyAESKey, publicKey);
}

/**
 * Receive a shared baby encryption key
 *
 * Process:
 * 1. Get the encrypted baby key from server
 * 2. Decrypt it with current user's private key
 * 3. Store in local secure storage
 */
export async function receiveSharedKey(
  encryptedBabyKey: string,
  userPrivateKey: CryptoKey
): Promise<CryptoKey> {
  return decryptKeyWithPrivateKey(encryptedBabyKey, userPrivateKey);
}

/**
 * Create a new baby profile with encryption key
 *
 * Returns both the AES key for local use and the encrypted version
 * for storage on the server (encrypted with user's public key)
 */
export async function createBabyKeyPair(
  userPublicKey: string
): Promise<{ aesKey: CryptoKey; encryptedKey: string }> {
  const aesKey = await generateAESKey();
  const publicKey = await importPublicKey(userPublicKey);
  const encryptedKey = await encryptKeyWithPublicKey(aesKey, publicKey);

  return { aesKey, encryptedKey };
}

/**
 * Re-encrypt all baby keys when user changes their password
 *
 * This is needed because changing password generates new RSA keys,
 * so all baby keys need to be re-encrypted with the new public key.
 */
export async function reEncryptBabyKeys(
  babyKeys: Map<string, CryptoKey>, // babyId -> AES key
  newPublicKey: string
): Promise<Map<string, string>> {
  const newEncryptedKeys = new Map<string, string>();
  const publicKey = await importPublicKey(newPublicKey);

  for (const [babyId, aesKey] of babyKeys) {
    const encryptedKey = await encryptKeyWithPublicKey(aesKey, publicKey);
    newEncryptedKeys.set(babyId, encryptedKey);
  }

  return newEncryptedKeys;
}

/**
 * Create a one-time share key for external sharing (pediatrician portal)
 *
 * This creates a temporary encryption key that can be used to decrypt
 * shared data without giving access to the actual baby profile key.
 */
export async function createShareKey(
  _babyAESKey: CryptoKey
): Promise<{ shareKey: CryptoKey; encryptedShareKey: string }> {
  // Generate a new temporary AES key for sharing
  const shareKey = await generateAESKey();

  // For the share link, we'll include the share key encrypted in a way
  // that can be passed via URL parameter and decrypted client-side
  const encryptedShareKey = await encryptShareKeyForUrl(shareKey);

  return { shareKey, encryptedShareKey };
}

/**
 * Encrypt a share key for inclusion in a URL
 *
 * Note: This is a simplified approach. In production, you might want
 * to use a more sophisticated key derivation scheme.
 */
async function encryptShareKeyForUrl(shareKey: CryptoKey): Promise<string> {
  // Export the key
  const keyData = await exportAESKey(shareKey);
  return keyData; // Base64 encoded
}

/**
 * Decrypt a share key from a URL parameter
 */
export async function decryptShareKeyFromUrl(
  encryptedKey: string
): Promise<CryptoKey> {
  return importAESKey(encryptedKey);
}

/**
 * Verify that a key can decrypt a test string
 *
 * Useful for validating that key import/export worked correctly
 */
export async function verifyKeyWorks(aesKey: CryptoKey): Promise<boolean> {
  try {
    const testData = 'test-encryption-string';
    const encoder = new TextEncoder();
    const dataBuffer = encoder.encode(testData);

    const iv = crypto.getRandomValues(new Uint8Array(12));

    const encrypted = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      aesKey,
      dataBuffer
    );

    const decrypted = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      aesKey,
      encrypted
    );

    const decoder = new TextDecoder();
    const decryptedText = decoder.decode(decrypted);

    return decryptedText === testData;
  } catch {
    return false;
  }
}

/**
 * Derive an AES key from an invite token
 *
 * This allows us to encrypt the baby key for transfer via invite
 * without requiring the invitee's public key (which may not exist yet)
 */
async function deriveKeyFromInviteToken(token: string): Promise<CryptoKey> {
  const encoder = new TextEncoder();
  const tokenBytes = encoder.encode(token);

  // Import as PBKDF2 key material
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    tokenBytes,
    'PBKDF2',
    false,
    ['deriveKey']
  );

  // Use a fixed salt for deterministic key derivation
  // (the token itself provides the entropy)
  const salt = encoder.encode('myTotTot-invite-key-v1');

  // Derive AES key
  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt,
      iterations: 100000,
      hash: 'SHA-256',
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

/**
 * Encrypt a baby's AES key for an invite
 *
 * The key is encrypted using a key derived from the invite token.
 * Only someone with the token can decrypt the baby key.
 */
export async function encryptBabyKeyForInvite(
  babyAESKey: CryptoKey,
  inviteToken: string
): Promise<string> {
  // Export the baby key
  const babyKeyData = await exportAESKey(babyAESKey);
  const babyKeyBytes = base64ToArrayBuffer(babyKeyData);

  // Derive encryption key from token
  const encryptionKey = await deriveKeyFromInviteToken(inviteToken);

  // Generate random IV
  const iv = crypto.getRandomValues(new Uint8Array(12));

  // Encrypt
  const encrypted = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    encryptionKey,
    babyKeyBytes
  );

  // Combine IV + ciphertext and encode as base64
  const combined = new Uint8Array(iv.length + encrypted.byteLength);
  combined.set(iv, 0);
  combined.set(new Uint8Array(encrypted), iv.length);

  return arrayBufferToBase64(combined.buffer);
}

/**
 * Decrypt a baby's AES key from an invite
 *
 * Uses the invite token to derive the decryption key
 */
export async function decryptBabyKeyFromInvite(
  encryptedBabyKey: string,
  inviteToken: string
): Promise<CryptoKey> {
  // Decode the combined IV + ciphertext
  const combined = new Uint8Array(base64ToArrayBuffer(encryptedBabyKey));

  // Extract IV (first 12 bytes) and ciphertext
  const iv = combined.slice(0, 12);
  const ciphertext = combined.slice(12);

  // Derive decryption key from token
  const decryptionKey = await deriveKeyFromInviteToken(inviteToken);

  // Decrypt
  const decrypted = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv },
    decryptionKey,
    ciphertext
  );

  // Import as AES key
  const babyKeyBase64 = arrayBufferToBase64(decrypted);
  return importAESKey(babyKeyBase64);
}
